import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { PaginatedTransactionResult, Transaction, TransactionFilters } from "@/types/payments";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { toTransactionQueryFilters } from "@/services/transactionService";

// Mirrors the transaction-api DEFAULT_PAGE_SIZE (issue #363). Sort/tabs are applied client-side
// to the current page — the persisted store is paginated and filtered server-side.
export const TRANSACTIONS_PAGE_SIZE = 50;

/**
 * Loads a single server page of persisted transactions (issue #363) via TanStack Query, keyed on
 * the selected accounts + page + filters. Selecting exactly one account fetches that account's
 * page; selecting several fetches the company-wide page narrowed server-side by `accountIds`
 * (FIN-802), so pagination and the total cover exactly those accounts. Selecting none means every
 * account. Description/amount/date filters are pushed down the same way via
 * `toTransactionQueryFilters`; `matched` stays a client-side/page-scoped filter (see
 * useTransactionFilter.ts) since it's derived, not a stored column.
 *
 * The query does not depend on the account list: each row carries its own account label, so a
 * failed account load never hides the transactions (FIN-802, EC5).
 *
 * `setTransactions` still accepts a `React.SetStateAction<Transaction[]>` so optimistic matching
 * updates keep working — it writes straight into the query cache for the active page.
 */
export function useTransactionLoader(
  selectedAccounts: string[],
  filters: TransactionFilters,
) {
  const [page, setPage] = useState(1);
  const pageSize = TRANSACTIONS_PAGE_SIZE;
  const queryClient = useQueryClient();
  const transactionService = ServiceRegistry.getTransactionService();

  const singleAccountId = selectedAccounts.length === 1 ? selectedAccounts[0] : null;
  const subsetKey = useMemo(
    () => (selectedAccounts.length > 1 ? [...selectedAccounts].sort().join(',') : ''),
    [selectedAccounts],
  );
  const queryFilters = useMemo(
    () => ({
      ...toTransactionQueryFilters(filters),
      ...(subsetKey ? { accountIds: subsetKey.split(',') } : {}),
    }),
    [filters, subsetKey],
  );
  const queryFiltersKey = JSON.stringify(queryFilters);

  const queryKey = useMemo(
    () => ['transactions', singleAccountId ?? 'all', page, pageSize, queryFiltersKey] as const,
    [singleAccountId, page, pageSize, queryFiltersKey],
  );

  // Server-pushed filters narrow the whole result set, not just the current page — reset back to
  // page 1 whenever they change so the user isn't stranded on a now out-of-range page number.
  const previousFiltersKey = useRef(queryFiltersKey);
  useEffect(() => {
    if (previousFiltersKey.current !== queryFiltersKey) {
      previousFiltersKey.current = queryFiltersKey;
      setPage(1);
    }
  }, [queryFiltersKey]);

  const { data, isLoading, isError, refetch } = useQuery<PaginatedTransactionResult>({
    queryKey,
    queryFn: () =>
      singleAccountId
        ? transactionService.getTransactionsForAccount(singleAccountId, page, pageSize, queryFilters)
        : transactionService.getTransactionsForAllAccounts(page, pageSize, queryFilters),
    placeholderData: keepPreviousData,
  });

  const transactions = data?.transactions ?? [];
  const total = data?.total ?? 0;

  // Optimistic local updates (e.g. after a manual match) write into the active page's cache entry
  // so the change survives until the next refetch, matching the previous useState contract.
  const setTransactions = useCallback(
    (update: React.SetStateAction<Transaction[]>) => {
      queryClient.setQueryData<PaginatedTransactionResult>(queryKey, (prev) => {
        const prevTransactions = prev?.transactions ?? [];
        const nextTransactions =
          typeof update === 'function'
            ? (update as (t: Transaction[]) => Transaction[])(prevTransactions)
            : update;
        return {
          transactions: nextTransactions,
          total: prev?.total ?? nextTransactions.length,
          page: prev?.page ?? page,
          pageSize: prev?.pageSize ?? pageSize,
        };
      });
    },
    [queryClient, queryKey, page, pageSize],
  );

  const loadTransactions = useCallback(async (): Promise<Transaction[]> => {
    const result = await refetch();
    return result.data?.transactions ?? [];
  }, [refetch]);

  return {
    transactions,
    setTransactions,
    loadingTransactions: isLoading,
    transactionsError: isError ? 'Failed to load transaction data. Please try again later.' : null,
    loadTransactions,
    // Pagination surface (issue #363) — additive; existing consumers ignore these.
    page,
    pageSize,
    total,
    setPage,
  };
}
